import { COMMAND_CENTER_ROOT } from './commandCenterRoutes'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, isAuthError } from '../../api/client'
import {
  CreatePortalFlow, createPortal, humanizePortalError, portalDetailPath,
  PORTAL_FACTORY_ROOT, PORTAL_REGISTRY_ROOT,
  type PortalDraft, type PortalRecord,
} from './olivium/portal-factory'

/**
 * Create-portal route wrapper.
 *
 *   /square/command-center/olivium/portal-factory/new
 *
 * Owns the persisted create (createPortal → sq_project_portals). Surfaces
 * honest saving / auth-required / backend-not-migrated states; on success
 * exposes the real saved record + creation-receipt summary. Never fakes a
 * saved portal.
 */
export default function SquareCodePortalCreateRoute() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)
  const [saved, setSaved] = useState<{ portal: PortalRecord; modulesAttached: number; receiptSummary: string | null } | null>(null)
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([])

  // Load the operator's canonical projects for the optional link selector.
  // Failure is non-fatal — the selector simply doesn't appear (portal
  // creation stays fully usable standalone).
  useEffect(() => {
    let cancelled = false
    void api.projects
      .list()
      .then((r) => { if (!cancelled) setProjects((r.projects || []).map((p) => ({ id: p.id, name: p.name }))) })
      .catch(() => { /* selector stays hidden; standalone create still works */ })
    return () => { cancelled = true }
  }, [])

  const onSubmit = async (draft: PortalDraft) => {
    setSaving(true); setError(null); setAuthRequired(false)
    try {
      // Surface the REAL server response — modules actually attached and the
      // real creation-receipt summary, never a reconstructed confirmation.
      const result = await createPortal(draft)
      setSaved(result)
    } catch (err) {
      if (isAuthError(err)) setAuthRequired(true)
      else setError(humanizePortalError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <CreatePortalFlow
      initialTemplateKey={params.get('template') ?? undefined}
      onSubmit={onSubmit}
      saving={saving}
      error={error}
      authRequired={authRequired}
      saved={saved}
      projects={projects}
      onHome={() => navigate(COMMAND_CENTER_ROOT)}
      onBackCommandCenter={() => navigate(COMMAND_CENTER_ROOT)}
      onOpenFactory={() => navigate(PORTAL_FACTORY_ROOT)}
      onOpenRegistry={() => navigate(PORTAL_REGISTRY_ROOT)}
      onOpenPortal={(id) => navigate(portalDetailPath(id))}
    />
  )
}
